import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import {
  type CategoryNode,
  useCategoryTreeConversion,
} from '../../category-tree';
import { type SchoolInfoCategoryTree } from '../types';

/**
 * 학교급 선택 관리 hook
 *
 * CategoryTree root를 받아서 학교급 레벨을 관리하고,
 * 선택된 학교급 노드를 다음 hook(useGradeState)에 전달합니다.
 */
export const useSelectSchoolType = ({
  initialSchoolTypeId,
  schoolInfoTree,
}: {
  initialSchoolTypeId?: string;
  schoolInfoTree: SchoolInfoCategoryTree;
}) => {
  const { categoryToMenuItem } = useCategoryTreeConversion();

  const schoolInfoNodes = useMemo(() => {
    return categoryToMenuItem(schoolInfoTree).subNodes;
  }, [schoolInfoTree, categoryToMenuItem]);

  const [selectedSchoolTypeNode, setSelectedSchoolTypeNode] = useState<
    CategoryNode | undefined
  >(() => schoolInfoNodes.find(node => node.nodeId === initialSchoolTypeId));

  const [error, setError] = useState<string>();
  const { t } = useTranslation(['build', 'common']);

  // 검증
  const validate = useCallback((): boolean => {
    if (schoolInfoNodes.length > 0 && !selectedSchoolTypeNode) {
      setError(t('build:schoolInfo.requiredField'));
      return false;
    }
    setError(undefined);
    return true;
  }, [schoolInfoNodes, selectedSchoolTypeNode, t]);

  // 에러 클리어
  const clearError = useCallback(() => {
    setError(undefined);
  }, []);

  return {
    selectedSchoolTypeNode,
    setSelectedSchoolTypeNode,
    schoolInfoNodes,
    error,
    validate,
    clearError,
  };
};
